import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'

import apiClient from '@/libs/apiClient'
import type { Offer } from '@/types/offer'

interface Pagination {
  currentPage: number
  lastPage: number
  perPage: number
  total: number
}

interface OfferState {
  offers: Offer[]
  pagination: Pagination | null
  loading: boolean
  actionLoading: boolean
  error: string | null
}

const initialState: OfferState = {
  offers: [],
  pagination: null,
  loading: false,
  actionLoading: false,
  error: null
}

// ============================
// Fetch all offers
// ============================
export const fetchOffers = createAsyncThunk<
  { offers: Offer[]; pagination: Pagination },
  { limit?: number } | undefined,
  { rejectValue: string }
>('offers/fetchOffers', async (params = {}, { rejectWithValue }) => {
  try {
    const limit = params.limit || 1000
    const res = await apiClient.get('/admin/v1/offers', { params: { limit } })
    const json = res.data

    const pagination: Pagination = {
      currentPage: json.meta.currentPage,
      lastPage: json.meta.lastPage,
      perPage: json.meta.perPage,
      total: json.meta.total
    }

    return { offers: json.data, pagination }
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch offers')
  }
})

// ============================
// Add Offer
// ============================
export const addOffer = createAsyncThunk<Offer, FormData, { rejectValue: string }>(
  'offers/addOffer',
  async (formData, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/admin/v1/offers/add', formData)
      const json = res.data

      return json.data as Offer
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to add offer')
    }
  }
)

// ============================
// Edit Offer
// ============================
export const editOffer = createAsyncThunk<Offer, { id: string; formData: FormData }, { rejectValue: string }>(
  'offers/editOffer',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`/admin/v1/offers/${id}/edit`, formData)
      const json = res.data

      return json.data as Offer
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to edit offer')
    }
  }
)

// ============================
// Delete Offer
// ============================
export const deleteOffer = createAsyncThunk<string, string, { rejectValue: string }>(
  'offers/deleteOffer',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/admin/v1/offers/${id}/delete`)

      return id
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to delete offer')
    }
  }
)

// ============================
// Toggle Offer Status
// ============================
export const toggleOfferStatus = createAsyncThunk<Offer, { id: string; is_enabled: boolean }, { rejectValue: string }>(
  'offers/toggleOfferStatus',
  async ({ id, is_enabled }, { rejectWithValue }) => {
    try {
      const endpoint = is_enabled ? 'enable' : 'disable'
      const res = await apiClient.post(`/admin/v1/offers/${id}/${endpoint}`)
      const json = res.data

      return json.data as Offer
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to toggle offer status')
    }
  }
)

// ============================
// Slice
// ============================
const offerSlice = createSlice({
  name: 'offers',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      // Fetch
      .addCase(fetchOffers.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchOffers.fulfilled, (state, action: PayloadAction<{ offers: Offer[]; pagination: Pagination }>) => {
        state.loading = false
        state.offers = action.payload.offers
        state.pagination = action.payload.pagination
      })
      .addCase(fetchOffers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to fetch offers'
      })

      // Add
      .addCase(addOffer.pending, state => {
        state.actionLoading = true
      })
      .addCase(addOffer.fulfilled, (state, action: PayloadAction<Offer>) => {
        state.actionLoading = false
        state.offers.unshift(action.payload)
        if (state.pagination) state.pagination.total += 1
      })
      .addCase(addOffer.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload || 'Failed to add offer'
      })

      // Edit
      .addCase(editOffer.pending, state => {
        state.actionLoading = true
      })
      .addCase(editOffer.fulfilled, (state, action: PayloadAction<Offer>) => {
        state.actionLoading = false
        state.offers = state.offers.map(o => (o.id === action.payload.id ? action.payload : o))
      })
      .addCase(editOffer.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload || 'Failed to edit offer'
      })

      // Delete
      .addCase(deleteOffer.pending, state => {
        state.actionLoading = true
      })
      .addCase(deleteOffer.fulfilled, (state, action: PayloadAction<string>) => {
        state.actionLoading = false
        state.offers = state.offers.filter(o => o.id !== action.payload)
        if (state.pagination) state.pagination.total -= 1
      })
      .addCase(deleteOffer.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload || 'Failed to delete offer'
      })

      // Toggle Status
      .addCase(toggleOfferStatus.pending, state => {
        state.actionLoading = true
      })
      .addCase(toggleOfferStatus.fulfilled, (state, action: PayloadAction<Offer>) => {
        state.actionLoading = false
        state.offers = state.offers.map(o => (o.id === action.payload.id ? action.payload : o))
      })
      .addCase(toggleOfferStatus.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload || 'Failed to toggle offer status'
      })
  }
})

export default offerSlice.reducer
